<script setup lang="ts">
import { AxiosError } from 'axios'
import type { NovaBoxSearchUserProps } from './NovaBoxSearchUser.types'

const props = withDefaults(defineProps<NovaBoxSearchUserProps>(), {
  compactMode: false,
})

const { gtEvent } = useGoogleTag()
const { t } = useI18n()
const { userStore, showSignInDialog } = useMembershipProcess()
const { showFollowErrorMessage, showUnfollowErrorMessage } = useFollow()
const commStore = useCommStore()
const postStore = usePostStore()
const isLoading = ref(false)
const { generateReCaptchaToken } = useReCaptcha()
const followerCount = computed(() =>
  useFormatThousandsSeparators(props.source.followerCount)
)
const on = computed(() =>
  props.compactMode
    ? postStore.posts.payload.flwUserSn === props.source.userSn
    : false
)

// 토글 팔로우
const handleOnToggleFollow = async () => {
  if (!userStore.isSignIn) {
    await showSignInDialog()
    return
  }
  if (props.source.followFlag === '0') {
    await handleOnFollow()
    return
  }
  if (props.source.followFlag === '1') {
    await handleOnUnFollow()
  }
}

// 팔로우
const handleOnFollow = async () => {
  try {
    isLoading.value = true
    const recaptchaToken = await generateReCaptchaToken('FOLLOW')
    await Promise.all([
      await useSleeper(),
      await commStore.reqFollow({
        flwUserSn: props.source.userSn,
        recaptchaToken,
      }),
    ])
    gtEvent('userAction', {
      eventCategory: '사용지',
      eventAction: '팔로우',
      eventLabel: t('mypage.myFollow.button.follow'),
      eventSlot: '검색 결과 > 유저',
      eventI18nAddr: useKoreanTranslation('mypage.myFollow.button.follow'),
      eventComponent: 'NovaButtonText',
    })
  } catch (err) {
    if (err instanceof AxiosError) {
      showFollowErrorMessage(err, props.source.userNcnm)
    }
  } finally {
    isLoading.value = false
  }
}

// 팔로잉 취소
const handleOnUnFollow = async () => {
  try {
    isLoading.value = true
    await Promise.all([
      await useSleeper(),
      await commStore.reqUnfollow({ flwUserSn: props.source.userSn }),
    ])
    gtEvent('userAction', {
      eventCategory: '사용지',
      eventAction: '언팔로우',
      eventLabel: t('mypage.myFollow.button.following'),
      eventSlot: '검색 결과 > 유저',
      eventI18nAddr: useKoreanTranslation('mypage.myFollow.button.following'),
      eventComponent: 'NovaButtonText',
    })
  } catch (err) {
    if (err instanceof AxiosError) {
      showUnfollowErrorMessage(err, props.source.userNcnm)
    }
  } finally {
    isLoading.value = false
  }
}

/**
 * 사용자 홈 가기
 * */
const handleOnGoUserHomePage = async () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '유저 홈으로 이동',
    eventLabel: '',
    eventSlot: '검색 결과 > 유저',
    eventI18nAddr: '',
    eventComponent: 'Button',
    userNcnm: props.source.userNcnm,
    userSn: props.source.userSn,
  })
  await useGoUserHome(props.source.userSn, () => {})
}
</script>

<template>
  <div :class="['box-user', { on }]">
    <div class="user">
      <div class="user-portrait">
        <NovaPortraitContainer
          :image-url="source.userProfl"
          :size="'60'"
          @click="handleOnGoUserHomePage"
        />

        <NovaCreatorGradeBadge
          v-if="source.crtrSpclGradCode"
          :creator-grade="source.crtrSpclGradCode"
          :creator-organization="source.crtrOrgnztTyCode"
          class="creator-badge"
        />
      </div>
      <h4 class="user-name" @click="handleOnGoUserHomePage">
        {{ source.userNcnm }}
      </h4>
    </div>

    <div class="actions">
      <p class="follower-count-warp">
        <span class="follower-count">{{ followerCount }}</span>
        <span class="unit">{{ $t('follower') }}</span>
      </p>
      <!--      <NovaButtonText
        :on="source.followFlag === '1'"
        :label="$t(source.followFlag === '0' ? 'follow' : 'following')"
        :theme="'sky-blue'"
        :size="32"
        :loading="isLoading"
        class="action"
        :disabled="userStore.user?.userSn === source.userSn"
        @click.stop="handleOnToggleFollow"
      />-->

      <div class="button-area">
        <NovaButtonText
          v-if="
            source.blockingAt === 'N' &&
            source.blockedAt === 'N' &&
            source.userSn !== userStore.user?.userSn
          "
          :on="source.followFlag === '1'"
          :label="
            $t(
              source.followFlag === '0'
                ? 'mypage.myFollow.button.follow'
                : 'mypage.myFollow.button.following'
            )
          "
          :theme="'sky-blue'"
          :loading="isLoading"
          @click.stop="handleOnToggleFollow"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.box-user {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 13px 18px 12px 17px;
  border-radius: 18px;
  background-color: $color-bg-3;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      &:after {
        width: calc(100% + 7px + 7px);
        height: calc(100% + 7px + 7px);
        border-radius: 22px;
        background-color: $color-text-9;
      }
    }
  }

  &:active,
  &.on {
    &:after {
      width: calc(100% + 3px + 3px);
      height: calc(100% + 3px + 3px);
      border-radius: $radius-10;
      background-color: $color-sky-blue;
    }

    .user .user-name {
      color: $color-sky-blue;
    }
  }

  &:after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    content: '';
    border-radius: 18px;
    border: 0 solid hex-to-rgba($color-text-9, 0);
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: -1;

    @include transition(
      width 0.14s ease-in-out,
      height 0.14s ease-in-out,
      background-color 0.14s ease-in-out
    );
  }

  .user {
    display: flex;
    align-items: center;
    gap: 10px;

    .user-portrait {
      position: relative;
      flex-shrink: 0;

      .creator-badge {
        position: absolute;
        left: 50%;
        bottom: -20px;
        transform: translateX(-50%);
      }
    }

    .user-name {
      flex-grow: 1;
      @include text-style($text-body-14-bold);
      line-height: 26px;
      @include ellipsis(2);
      @include transition(color 0.2s ease-in-out);
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .follower-count-warp {
      display: flex;
      align-items: center;
      gap: 6px;
      min-height: 40px;
      @include text-style($text-body-12-regular);
      line-height: 24px;

      .follower-count {
        font-weight: 700;
        color: $color-black;
      }

      .unit {
        color: $color-text-9;
        @include ellipsis(1);
      }
    }

    .button-area {
      min-height: 40px;
    }
  }

  @include mobile {
    gap: 10px;

    .user {
      flex-direction: column;
      gap: 5px;

      .user-name {
        height: 52px;
      }
    }

    .actions {
      flex-direction: column;
      gap: 6px;

      .action {
        width: 100%;
      }
    }
  }
}
</style>
